import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { commonTextStyle } from "../InternalStyle";
import InternalCarousal from "../Carousal-ExpandedView/InternalCarousal";
import { useSelector } from "react-redux";
import ListViewDesign from "../ListView/ListViewDesign";
import useThemeRender from "../../../theme/useThemeRender";
import PropTypes from "prop-types";

// Memoized component to prevent unnecessary re-renders
const InternalCommonSection = React.memo(
  ({
    data,
    grid,
    selectedSection,
    subPageId,
    slug,
    cardButtonDisable,
    chapter = false,
  }) => {
    const { documents, links, videos } = data;

    const { search, sector, variety, country, flashColor, ipOwnerShip } =
      useSelector((store) => store.internalPage);

    const filteredRows = useMemo(() => {
      const normalize = (val) => val?.toLowerCase() ?? "";

      const matchLabel = (itemValue, filter) =>
        !filter?.label || normalize(itemValue) === normalize(filter.label);

      const matchArrayLabel = (array, filter) =>
        !filter?.label ||
        (array || []).some(
          (v) => normalize(v?.name) === normalize(filter.label)
        );

      const matchesAll = (item) => {
        const matchesSearch = Object.values(item || {}).some((val) =>
          String(val)
            .toLowerCase()
            .includes(search?.toLowerCase() || "")
        );

        return (
          matchesSearch &&
          matchArrayLabel(item.sectors, sector) &&
          matchArrayLabel(item.countries, country) &&
          matchLabel(item.variety, variety) &&
          matchLabel(item.fleshColor, flashColor) &&
          matchLabel(item.ipOwnerShip, ipOwnerShip)
        );
      };

      const filterData = (items) => items?.filter(matchesAll);

      return {
        documents: filterData(documents),
        links: filterData(links),
        videos: filterData(videos),
      };
    }, [
      documents,
      links,
      videos,
      search,
      sector,
      country,
      variety,
      flashColor,
      ipOwnerShip,
    ]);

    useEffect(() => {
      if (!selectedSection) return;
      const element = document.getElementById(selectedSection);
      if (element) {
        const offset = 150;
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: elementPosition, behavior: "smooth" });
      }
    }, [selectedSection]);

    return (
      <section
        id={data?.sectionName}
        style={{
          display: "flex",
          width: "100%",
          padding: "24px",
          flexDirection: "column",
          alignItems: "flex-start",
          borderRadius: "8px",
          backgroundColor: useThemeRender("backgroundColorLayer2"),
          boxShadow: "0px 0px 24px rgba(0, 92, 188, 0.08)",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}
        >
          <Typography
            sx={{
              ...commonTextStyle,
              fontSize: "24px",
              lineHeight: "32px",
              color: useThemeRender("textColorLayer2"),
            }}
          >
            {data?.sectionName}
          </Typography>
        </Box>

        {/* Grid View */}
        {grid === "tile" ? (
          <Box sx={{ width: "100%" }}>
            {filteredRows?.documents?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <InternalCarousal
                  data={filteredRows?.documents}
                  content={"Documents"}
                  flexDirection={"column"}
                  heading={"Documents"}
                  isHeadingBig={false}
                  subPageId={subPageId}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
            {filteredRows?.videos?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <InternalCarousal
                  data={filteredRows?.videos}
                  content={"Videos"}
                  flexDirection={"column"}
                  heading={"Videos"}
                  isHeadingBig={false}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
            {filteredRows?.links?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <InternalCarousal
                  data={filteredRows?.links}
                  content={"Links"}
                  flexDirection={"column"}
                  heading={"Links"}
                  isHeadingBig={false}
                  subPageId={subPageId}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
          </Box>
        ) : (
          // List View
          <Box sx={{ width: "100%", marginTop: "16px" }}>
            {filteredRows?.documents?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <ListViewDesign
                  data={filteredRows?.documents}
                  content={"Documents"}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
            {filteredRows?.videos?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <ListViewDesign
                  data={filteredRows?.videos}
                  content={"Videos"}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
            {filteredRows?.links?.length > 0 && (
              <Box sx={{ marginBottom: "16px" }}>
                <ListViewDesign
                  data={filteredRows?.links}
                  content={"Links"}
                  slug={slug}
                  chapter={chapter}
                  cardButtonDisable={cardButtonDisable}
                />
              </Box>
            )}
          </Box>
        )}
      </section>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.data === nextProps.data &&
      prevProps.grid === nextProps.grid &&
      prevProps.selectedSection === nextProps.selectedSection &&
      prevProps.subPageId === nextProps.subPageId &&
      prevProps.chapter === nextProps.chapter
    );
  }
);

InternalCommonSection.propTypes = {
  data: PropTypes.shape({
    sectionName: PropTypes.string.isRequired,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        sector: PropTypes.string,
        country: PropTypes.string,
        variety: PropTypes.string,
        fleshColor: PropTypes.string,
        ipOwnerShip: PropTypes.string,
      })
    ),
    links: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        sector: PropTypes.string,
        country: PropTypes.string,
        variety: PropTypes.string,
        fleshColor: PropTypes.string,
        ipOwnerShip: PropTypes.string,
      })
    ),
    videos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        sector: PropTypes.string,
        country: PropTypes.string,
        variety: PropTypes.string,
        fleshColor: PropTypes.string,
        ipOwnerShip: PropTypes.string,
      })
    ),
  }).isRequired,
  grid: PropTypes.oneOf(["tile", "list"]).isRequired,
  selectedSection: PropTypes.string,
  chapter: PropTypes.bool,
  subPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default InternalCommonSection;
